import AccountCircle from "@mui/icons-material/AccountCircle";
import Close from "@mui/icons-material/Close";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityType as PrismaActivityType } from "encamp-prisma/generated/client";
import type { FacilityPilotProgramStatesQuery } from "generated-graphql/graphql";
import { ActivityType } from "generated-graphql/graphql";
import React, { useMemo } from "react";
import { getLinearStatusColor } from "util/linearStatusColors";
import {
  handleLinearTagClick,
  handleLinearTagDoubleClick,
  isLinearIssue,
} from "./linearTagHelpers";

type StateRow =
  FacilityPilotProgramStatesQuery["facilityPilotProgramStates"]["items"][number];
type PilotFacilityRow = StateRow["pilotFacilities"][number];

interface DetailPanelContentProps {
  pilotFacilities: PilotFacilityRow[];
  onRemove: (configId: string) => void;
  removeLoading: boolean;
  linearStatusMap: { [identifier: string]: { status: string; url: string } };
}

export const DetailPanelContent = React.memo(function DetailPanelContent({
  pilotFacilities,
  onRemove,
  removeLoading,
  linearStatusMap,
}: DetailPanelContentProps) {
  const columns: GridColDef<PilotFacilityRow>[] = useMemo(
    () => [
      {
        field: "facilityName",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack>
            <Typography variant="body2">{row.facilityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.streetAddress1, row.city, row.facilityState]
                .filter(Boolean)
                .join(", ")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "tenantName",
        headerName: "Organization",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{row.tenantName}</Typography>
            {row.isDeniedTenant && (
              <Tooltip title="Tenant is on deny list, but this facility is explicitly allowed via ALLOW_FACILITY rule">
                <Chip label="Allowed (Tenant Denied)" color="success" size="small" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "reportEncampStatus",
        headerName: "Encamp Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.reportEncampStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Chip
              label={prettyPrintEnumValue(row.reportEncampStatus)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "workflowStatus",
        headerName: "Workflow Status",
        width: 150,
        renderCell: ({ row }) => {
          if (!row.workflowStatus) {
            return (
              <Typography variant="body2" color="text.secondary">
                Not Started
              </Typography>
            );
          }
          const color =
            row.workflowStatus === "Completed"
              ? "success"
              : row.workflowStatus === "Error"
              ? "error"
              : row.workflowStatus === "InProgress"
              ? "info"
              : "default";
          return (
            <Chip
              label={prettyPrintEnumValue(row.workflowStatus)}
              size="small"
              color={color}
            />
          );
        },
      },
      {
        field: "currentActivityStep",
        headerName: "Activity Step",
        width: 180,
        renderCell: ({ row }) => {
          const isStaffActivity =
            row.currentActivityType === ActivityType.Staff;

          if (row.currentActivityDisplayName) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                {isStaffActivity && (
                  <AccountCircle
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                )}
                <Typography variant="body2">
                  {row.currentActivityDisplayName}
                </Typography>
              </Stack>
            );
          }
          if (row.currentActivityType) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                {isStaffActivity && (
                  <AccountCircle
                    sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                  />
                )}
                <Typography variant="body2">
                  {getActivityTitleOnly(
                    {
                      type: row.currentActivityType as PrismaActivityType,
                      description: null,
                    },
                    undefined,
                    undefined
                  )}
                </Typography>
              </Stack>
            );
          }
          return (
            <Typography variant="body2" color="text.secondary">
              Not Started
            </Typography>
          );
        },
      },
      {
        field: "workflowTags",
        headerName: "Workflow Tags",
        width: 200,
        flex: 1,
        renderCell: ({ row }) => {
          const tags = row.workflowTags;
          if (!tags || tags.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {tags.map((tag: { id: string; name: string }) => {
                const isLinear = isLinearIssue(tag.name);
                const issueInfo = isLinear
                  ? linearStatusMap[tag.name.toUpperCase()]
                  : null;
                const backgroundColor = issueInfo
                  ? getLinearStatusColor(issueInfo.status)
                  : undefined;

                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    onClick={(e) =>
                      handleLinearTagClick(e, tag.name, linearStatusMap)
                    }
                    onDoubleClick={(e) =>
                      handleLinearTagDoubleClick(e, tag.name, linearStatusMap)
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      flexShrink: 0,
                      maxWidth: 80,
                      backgroundColor,
                      cursor: isLinear && issueInfo ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        padding: "0 8px",
                        lineHeight: 1,
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        field: "configCreatedBy",
        headerName: "Added By",
        width: 150,
        renderCell: ({ row }) => (
          <Tooltip
            title={`Added: ${new Date(row.configCreatedAt).toLocaleString()}`}
          >
            <Typography variant="body2">{row.configCreatedBy}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          ...(params.row.reportId
            ? [
                <GridActionsCellItem
                  key="view"
                  icon={
                    <Tooltip title="View report details">
                      <OpenInNew />
                    </Tooltip>
                  }
                  label="View Report"
                  onClick={() =>
                    window.open(
                      `/staff/fulfillment/r/${params.row.reportId}`,
                      "_blank"
                    )
                  }
                />,
              ]
            : []),
          <GridActionsCellItem
            key="remove"
            icon={
              <Tooltip title="Remove from pilot program">
                <Close />
              </Tooltip>
            }
            label="Remove"
            onClick={() => onRemove(params.row.configId)}
            disabled={removeLoading}
          />,
        ],
      },
    ],
    [onRemove, removeLoading, linearStatusMap]
  );

  return (
    <Box sx={{ p: 2, pl: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Pilot Facilities ({pilotFacilities.length})
      </Typography>
      <DataGrid<PilotFacilityRow>
        rows={pilotFacilities}
        columns={columns}
        getRowId={(row) => row.configId}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1.5,
            alignItems: "center",
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important",
          },
          // Show actions always (not just on hover)
          "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']":
            {
              opacity: 1,
            },
        }}
      />
    </Box>
  );
});
